import { chSupabase } from '../lib/supabase';
import type { ChProfile, ChProject, ChBranding, ChUserRole, ChFollowers } from '../types';

export const chProfileService = {
  async getCurrent(): Promise<ChProfile | null> {
    const { data: { user } } = await chSupabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await chSupabase
      .from('ch_profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();

    if (error) throw error;
    return data as ChProfile | null;
  },

  async getAll(): Promise<ChProfile[]> {
    const { data, error } = await chSupabase
      .from('ch_profiles')
      .select('*')
      .order('name');

    if (error) throw error;
    return (data || []) as ChProfile[];
  },

  async updateRole(userId: string, role: ChUserRole): Promise<void> {
    const { error } = await chSupabase
      .from('ch_profiles')
      .update({ role })
      .eq('id', userId);

    if (error) throw error;
  },

  /** Revokes Content Hub access; the login and past activity stay. */
  async removeAccess(userId: string): Promise<void> {
    const { error: linksError } = await chSupabase
      .from('ch_user_projects')
      .delete()
      .eq('user_id', userId);
    if (linksError) throw linksError;

    const { error } = await chSupabase
      .from('ch_profiles')
      .delete()
      .eq('id', userId);
    if (error) throw error;
  },
};

export const chProjectService = {
  async getAll(): Promise<ChProject[]> {
    const { data, error } = await chSupabase
      .from('ch_projects')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as ChProject[];
  },

  async getBySlug(slug: string): Promise<ChProject | null> {
    const { data, error } = await chSupabase
      .from('ch_projects')
      .select('*')
      .eq('slug', slug)
      .maybeSingle();

    if (error) throw error;
    return data as ChProject | null;
  },

  async create(project: Omit<ChProject, 'id' | 'created_at'>): Promise<ChProject> {
    const { data, error } = await chSupabase
      .from('ch_projects')
      .insert(project)
      .select()
      .single();

    if (error) throw error;
    return data as ChProject;
  },

  async update(id: string, updates: Partial<ChProject>): Promise<void> {
    const { error } = await chSupabase
      .from('ch_projects')
      .update(updates)
      .eq('id', id);

    if (error) throw error;
  },

  async delete(id: string): Promise<void> {
    // A delete the policies refuse matches no row and returns no error
    const { data, error } = await chSupabase
      .from('ch_projects')
      .delete()
      .eq('id', id)
      .select('id');

    if (error) throw error;
    if (!data || data.length === 0) throw new Error('Sem permissão para eliminar este projeto');
  },

  /** How much a delete would take with it (posts cascade). */
  async countPosts(projectId: string): Promise<number> {
    const { count, error } = await chSupabase
      .from('ch_posts')
      .select('id', { count: 'exact', head: true })
      .eq('project_id', projectId);

    if (error) throw error;
    return count || 0;
  },

  /** Admin and Account both keep the follower counts up to date. */
  async setFollowers(projectId: string, followers: ChFollowers): Promise<ChFollowers> {
    const { data, error } = await chSupabase.rpc('ch_set_project_followers', {
      p_project_id: projectId,
      p_followers: followers,
    });
    if (error?.code === 'PGRST202') {
      throw new Error('Falta aplicar o SQL do Content Hub v2 no Supabase (20261028120000_content_hub_v2.sql)');
    }
    if (error) throw error;
    return (data || {}) as ChFollowers;
  },
};

export const chBrandingService = {
  async get(projectId: string): Promise<ChBranding | null> {
    const { data, error } = await chSupabase
      .from('ch_branding')
      .select('*')
      .eq('project_id', projectId)
      .maybeSingle();

    if (error) throw error;
    return data as ChBranding | null;
  },

  async upsert(branding: Partial<ChBranding> & { project_id: string }): Promise<void> {
    const { error } = await chSupabase
      .from('ch_branding')
      .upsert({ ...branding, updated_at: new Date().toISOString() });

    if (error) throw error;
  },

  async uploadFile(bucket: string, path: string, file: File): Promise<string> {
    const { error } = await chSupabase.storage
      .from(bucket)
      .upload(path, file, { upsert: true });

    if (error) throw error;

    const { data } = chSupabase.storage.from(bucket).getPublicUrl(path);
    return data.publicUrl;
  },
};

export const chUserProjectService = {
  /**
   * Members with their profile. Two queries: ch_user_projects points at
   * auth.users, not ch_profiles, so PostgREST can't embed the profile.
   */
  async getProjectMembers(projectId: string): Promise<{ user_id: string; project_id: string; profile: ChProfile }[]> {
    const { data, error } = await chSupabase
      .from('ch_user_projects')
      .select('user_id, project_id')
      .eq('project_id', projectId);

    if (error) throw error;
    const links = (data || []) as { user_id: string; project_id: string }[];
    if (links.length === 0) return [];

    const { data: profiles, error: profilesError } = await chSupabase
      .from('ch_profiles')
      .select('*')
      .in('id', links.map(l => l.user_id));
    if (profilesError) throw profilesError;

    const byId = new Map(((profiles || []) as ChProfile[]).map(p => [p.id, p]));
    // A client without a visible profile is still a member; skip only the unknown
    return links
      .filter(l => byId.has(l.user_id))
      .map(l => ({ ...l, profile: byId.get(l.user_id)! }));
  },

  async addMember(userId: string, projectId: string): Promise<void> {
    const { error } = await chSupabase
      .from('ch_user_projects')
      .insert({ user_id: userId, project_id: projectId });

    if (error) throw error;
  },

  async removeMember(userId: string, projectId: string): Promise<void> {
    const { error } = await chSupabase
      .from('ch_user_projects')
      .delete()
      .eq('user_id', userId)
      .eq('project_id', projectId);

    if (error) throw error;
  },
};
